import type * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { JSX } from 'react'

import { useQuery } from '@apollo/client/react'

import {
  ChannelAvatar,
  CheckIcon,
  ClockIcon,
  XIcon,
  Dialog,
  SquarePenIcon,
  Flex,
  IconButton,
  Sidebar,
  SimpleSpinner,
  Text,
  useMediaQuery,
  useSidebar,
  VisuallyHidden,
  WarningIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { PostCard_Post } from '~publish/components/PostCard'
import type { PostStatus } from '~publish/components/graphqlEnums'
import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { readFragment } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { getReadablePostStatus } from '~publish/helpers/posts/getReadablePostStatus'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import {
  NotesDismissGuardProvider,
  useNotesDismissGuard,
} from '~publish/hooks/notes/NotesDismissGuard'

import { GetPost } from '../PostDetailsDialog'
import { PostDetailsContent } from '../PostDetailsContent'
import { useCollaborationNotesPanel } from '../PostDetailsContent/useCollaborationNotesPanel'
import { isOptionalNotesSummaryError } from '~publish/hooks/notes/isOptionalNotesSummaryError'
import { PostErrorState } from '../PostErrorDialog'

import { useLoadPostGroup } from './useLoadPostGroup'
import type { GetPostGroupForPostGroupDetails } from './useLoadPostGroup'

import styles from './PostGroupDialog.module.css'

export { POST_GROUPS_UI_FLAG } from './postGroupsUiFlag'
export const POST_GROUP_SIDEBAR_COLLAPSE_MEDIA = '(width < 920px)'
export const POST_GROUP_SIDEBAR_COLLAPSE_MEDIA_WITH_NOTES = '(width < 1240px)'

const StatusIcon = ({
  status,
  hasScheduledTime,
}: {
  status: PostStatus
  hasScheduledTime: boolean
}): JSX.Element | null => {
  if (status === 'sent') {
    return <CheckIcon size="xsmall" color="success" aria-hidden />
  }
  if (status === 'error') {
    return <WarningIcon size="xsmall" color="critical" aria-hidden />
  }
  if (status === 'draft' || status === 'needs_approval') {
    // A draft / needs-approval post with no scheduled time has nothing to show.
    if (!hasScheduledTime) {
      return null
    }
    // A scheduled draft uses the draft icon rather than the clock reserved for
    // genuinely-queued posts.
    return <SquarePenIcon size="xsmall" color="subtle" aria-hidden />
  }
  return <ClockIcon size="xsmall" color="subtle" aria-hidden />
}

type PostGroupPosts = NonNullable<
  ResultOf<typeof GetPostGroupForPostGroupDetails>['postGroup']
>['posts']

type PostGroupChannelSidebarProps = {
  posts: PostGroupPosts
  selectedId: string
  onSelect: (postId: string, channelId: string, channelService: string) => void
  dateTimeFormatter: ReturnType<typeof useDateTimeFormatter>
}

const PostGroupChannelSidebar = ({
  posts,
  selectedId,
  onSelect,
  dateTimeFormatter,
}: PostGroupChannelSidebarProps): JSX.Element => {
  const { setOpen } = useSidebar()
  const { showPanel } = useCollaborationNotesPanel()
  const isSmallScreen = useMediaQuery(
    showPanel
      ? POST_GROUP_SIDEBAR_COLLAPSE_MEDIA_WITH_NOTES
      : POST_GROUP_SIDEBAR_COLLAPSE_MEDIA,
  )

  useEffect(() => {
    if (isSmallScreen) {
      setOpen(false)
    }
  }, [isSmallScreen, setOpen])

  return (
    <Sidebar
      collapsible="icon"
      aria-label="Channels in group"
      className={styles.sidebar}
    >
      <Sidebar.Header className={styles.sidebarHeader}>
        <Text size="sm" color="subtle" className={styles.channelsLabel}>
          Channels ({posts.length})
        </Text>
        <Sidebar.Trigger className={styles.sidebarTrigger} />
      </Sidebar.Header>
      <Sidebar.Content>
        <Sidebar.List>
          {posts.map((post) => {
            const postData = readFragment(PostCard_Post, post)
            const isSelected = postData.id === selectedId
            const date =
              postData.status === 'sent' && postData.sentAt
                ? postData.sentAt
                : postData.dueAt
            return (
              <Sidebar.ListItem key={postData.id}>
                <Sidebar.Button
                  selected={isSelected}
                  aria-current={isSelected ? true : undefined}
                  onClick={(): void =>
                    onSelect(
                      postData.id,
                      postData.channel.id,
                      postData.channel.service,
                    )
                  }
                  className={styles.sidebarButton}
                  prefix={
                    <ChannelAvatar
                      size="small"
                      alt={`${getChannelNameForDisplay(postData.channel)}, ${postData.channel.service}`}
                      src={postData.channel.avatar ?? undefined}
                      channel={postData.channel.service}
                    />
                  }
                >
                  <Flex direction="column" gap="2xs">
                    <Text
                      weight="medium"
                      truncate
                      translate="no"
                      lineHeight="tight"
                    >
                      {getChannelNameForDisplay(postData.channel)}
                    </Text>
                    <Text
                      size="sm"
                      color="subtle"
                      lineHeight="tight"
                      className={styles.channelStatus}
                    >
                      <StatusIcon
                        status={postData.status}
                        hasScheduledTime={!!postData.dueAt}
                      />
                      <VisuallyHidden>
                        {getReadablePostStatus(postData.status)}
                      </VisuallyHidden>
                      {date
                        ? dateTimeFormatter(date, { includeTimeZone: false })
                        : ''}
                    </Text>
                  </Flex>
                </Sidebar.Button>
              </Sidebar.ListItem>
            )
          })}
        </Sidebar.List>
      </Sidebar.Content>
    </Sidebar>
  )
}

const FallbackHeader = (): JSX.Element => (
  <Dialog.Header className={styles.fallbackHeader}>
    <VisuallyHidden>
      <Dialog.Title>Post Details</Dialog.Title>
      <Dialog.Description>
        Detailed view of the post and any posts created together with it.
      </Dialog.Description>
    </VisuallyHidden>
    <Dialog.Close asChild>
      <IconButton
        data-post-details-close=""
        label="Close"
        onClick={undefined}
        size="small"
        variant="tertiary"
      >
        <XIcon />
      </IconButton>
    </Dialog.Close>
  </Dialog.Header>
)

const LoadingFallback = (): JSX.Element => (
  <Dialog.Body className={styles.body}>
    <FallbackHeader />
    <div className={styles.fallback}>
      <SimpleSpinner size="large" aria-label="Loading post details" />
    </div>
  </Dialog.Body>
)

const DetailPane = ({
  post,
}: {
  post: FragmentOf<typeof PostCard_Post>
}): JSX.Element => {
  const focusNotesComposer = useContext(NotesComposerFocusContext)
  return (
    <div className={styles.detailPane}>
      <PostDetailsContent post={post} focusNotesComposer={focusNotesComposer} />
    </div>
  )
}

/**
 * The post content owns the header and Close in each dialog view.
 */
const SinglePostBody = ({
  post,
}: {
  post: FragmentOf<typeof PostCard_Post>
}): JSX.Element => (
  <Dialog.Body className={styles.body}>
    <div className={styles.singleLayout}>
      <DetailPane post={post} />
    </div>
  </Dialog.Body>
)

/**
 * Plain single-post view backed by one GetPost query. Renders for ungrouped
 * posts, and as the degraded view when a group can't provide the opener post.
 */
const SinglePostDetails = ({ postId }: { postId: string }): JSX.Element => {
  const { showPanel } = useCollaborationNotesPanel()
  const { data, loading, error } = useQuery(GetPost, {
    variables: { id: postId, includeNotesSummary: showPanel },
    errorPolicy: 'all',
  })

  if (loading) {
    return <LoadingFallback />
  }

  const rawPost = data?.post
  if ((error && !isOptionalNotesSummaryError(error)) || !rawPost) {
    return (
      <Dialog.Body className={styles.body}>
        <FallbackHeader />
        <div className={styles.fallback}>
          <PostErrorState error={error} />
        </div>
      </Dialog.Body>
    )
  }

  return <SinglePostBody post={rawPost} />
}

/**
 * Group-aware view backed by one group query, which delivers the opener post
 * together with its siblings. Groups with more than one post get the channel
 * sidebar. If the group cannot provide the opener, it uses SinglePostDetails.
 */
const GroupPostDetails = ({
  postId,
  postGroupId,
}: {
  postId: string
  postGroupId: string
}): JSX.Element => {
  // The post the detail pane shows: starts as the post the dialog was opened
  // for, changes when a sibling is picked in the channel sidebar.
  const [selectedId, setSelectedId] = useState(postId)
  const { data, loading, error } = useLoadPostGroup(postGroupId)
  const dateTimeFormatter = useDateTimeFormatter()
  const organizationId = useOrganizationId()
  const accountId = useAccountId()

  const handleChannelSelect = useCallback(
    (
      selectedPostId: string,
      channelId: string,
      channelService: string,
    ): void => {
      setSelectedId(selectedPostId)
      if (organizationId && accountId) {
        BufferTracker.postCardActionClicked({
          organizationId,
          action: 'postGroupDetailsSwitchChannel-1',
          accountId,
          channelId,
          channel: channelService,
          postId: selectedPostId,
          clientName: 'publishWeb',
        })
      }
    },
    [organizationId, accountId],
  )

  if (loading) {
    return <LoadingFallback />
  }

  const groupPosts = data?.postGroup?.posts
  const openerFromGroup = groupPosts?.find(
    (groupPost) => readFragment(PostCard_Post, groupPost).id === postId,
  )
  if (
    (error && !isOptionalNotesSummaryError(error)) ||
    !groupPosts ||
    !openerFromGroup
  ) {
    return <SinglePostDetails postId={postId} />
  }

  // A group with one post has no siblings to list in the sidebar.
  if (groupPosts.length === 1) {
    return <SinglePostBody post={openerFromGroup} />
  }

  const selectedPost =
    groupPosts.find(
      (groupPost) => readFragment(PostCard_Post, groupPost).id === selectedId,
    ) ?? openerFromGroup

  return (
    <Dialog.Body className={styles.body}>
      <Sidebar.Provider>
        <div className={styles.layout}>
          <PostGroupChannelSidebar
            posts={groupPosts}
            selectedId={readFragment(PostCard_Post, selectedPost).id}
            onSelect={handleChannelSelect}
            dateTimeFormatter={dateTimeFormatter}
          />
          <DetailPane post={selectedPost} />
        </div>
      </Sidebar.Provider>
    </Dialog.Body>
  )
}

type PostGroupDialogProps = {
  /** The post the dialog was opened for. */
  postId: string
  /**
   * The opener post's group, when it has one. Openers read it off their card
   * fragment (`postGroup { id }`), so the dialog needs no lookup query of its
   * own — absent (ungrouped post), the dialog fetches the post directly.
   */
  postGroupId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Optional opener element, rendered as the dialog's Radix trigger so click
   * handling and aria wiring (haspopup/expanded) come for free. Openers that
   * can't host a trigger (e.g. dropdown menu items, which unmount when the
   * menu closes) leave this unset and drive `open` themselves.
   */
  trigger?: React.ReactElement
  /**
   * Focus target when the dialog closes. Menu-item openers unmount with
   * their menu, so Radix's default restore (the element focused at open
   * time) falls to document.body — pass the menu's persistent trigger
   * button instead. Openers using `trigger` don't need this: Radix
   * restores focus to the trigger automatically.
   */
  returnFocusTo?: React.RefObject<HTMLElement>
  /**
   * Focus the Notes composer when the dialog opens. Used by the Notes
   * entry point on Queue and Calendar cards.
   */
  focusNotesComposer?: boolean
}

/**
 * The group-aware post details dialog, opened in place by in-app affordances
 * (the card action menu, the calendar expand button) — the URL does not
 * change, so the route keeps its one-post-per-post-ID promise. Triggers that
 * can only express themselves as a URL (deep links, the composer toast) go
 * through the route-mounted single-post PostDetailsDialog instead.
 *
 * The post content owns the header in each view. A multi-post group also
 * shows the channel sidebar. The queries run only when Radix renders content.
 */
const NotesComposerFocusContext = createContext(false)

export function PostGroupDialog({
  postId,
  postGroupId,
  open,
  onOpenChange,
  trigger,
  returnFocusTo,
  focusNotesComposer = false,
}: PostGroupDialogProps): JSX.Element {
  return (
    <NotesDismissGuardProvider>
      <NotesComposerFocusContext.Provider value={focusNotesComposer}>
        <PostGroupDialogBody
          postId={postId}
          postGroupId={postGroupId}
          open={open}
          onOpenChange={onOpenChange}
          trigger={trigger}
          returnFocusTo={returnFocusTo}
          focusNotesComposer={focusNotesComposer}
        />
      </NotesComposerFocusContext.Provider>
    </NotesDismissGuardProvider>
  )
}

function PostGroupDialogBody({
  postId,
  postGroupId,
  open,
  onOpenChange,
  trigger,
  returnFocusTo,
  focusNotesComposer = false,
}: PostGroupDialogProps): JSX.Element {
  const contentRef = useRef<HTMLDivElement>(null)
  const dismissGuard = useNotesDismissGuard()

  const focusCloseOnOpen = (event: Event): void => {
    // Hand focus to the Notes panel's own effect, but only once that panel is
    // mounted and visible. While the post query is loading, and on error, the
    // panel never renders, so yielding here would leave focus outside the
    // dialog. Fall through to Close in that case; the panel's effect moves
    // focus to the composer if and when it mounts.
    const notesPanel = contentRef.current?.querySelector(
      '[data-collaboration-notes-panel]:not([hidden])',
    )
    if (focusNotesComposer && notesPanel) {
      event.preventDefault()
      return
    }
    const closeButton = contentRef.current?.querySelector<HTMLButtonElement>(
      '[data-post-details-close]',
    )
    if (closeButton) {
      event.preventDefault()
      closeButton.focus()
    }
  }

  const restoreFocusOnClose = (event: Event): void => {
    // null when the dialog's action unmounted the opener's card entirely
    // (e.g. the post was deleted) — let Radix's default attempt run then.
    if (returnFocusTo?.current) {
      event.preventDefault()
      returnFocusTo.current.focus()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger != null && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Content
        ref={contentRef}
        size="large"
        className={styles.content}
        onOpenAutoFocus={focusCloseOnOpen}
        onPointerDownOutside={dismissGuard.onPointerDownOutside}
        onEscapeKeyDown={dismissGuard.onEscapeKeyDown}
        onClickCapture={dismissGuard.onClickCapture}
        onClick={(event) =>
          dismissGuard.onComposerClick(event, () => onOpenChange(false))
        }
        onCloseAutoFocus={restoreFocusOnClose}
      >
        {postGroupId ? (
          <GroupPostDetails postId={postId} postGroupId={postGroupId} />
        ) : (
          <SinglePostDetails postId={postId} />
        )}
      </Dialog.Content>
    </Dialog>
  )
}
